/**
 * Numbers as the converter shows them, in the page's language: "80,596,000.00" (en),
 * "80.596.000,00" (de), "8,05,96,000.00" (hi). The store keeps plain strings like
 * "80596000.00" (share URLs, copy buttons); only what's displayed gets separators.
 * Same separators as the server-rendered tables (ConverterFormatHelper::SEPARATORS).
 */
export interface NumberSeparators {
  group: string;
  decimal: string;
  groupStyle: "thousand" | "lakh";
}

const ENGLISH: NumberSeparators = {
  group: ",",
  decimal: ".",
  groupStyle: "thousand",
};

export const numberSeparators = (locale: string): NumberSeparators => {
  try {
    // nu-latn: ASCII digits even where the language defaults to its own (bn)
    const sample = new Intl.NumberFormat(`${locale}-u-nu-latn`).format(
      123456789.5,
    );
    const decimal = sample.charAt(sample.length - 2);
    const whole = sample.slice(0, -2);
    if (/\d/.test(decimal) || !/\d$/.test(whole)) return ENGLISH;
    const group = whole.replace(/\d/g, "").charAt(0);
    // "12,34,56,789" (hi, bn) vs "123,456,789"
    const lakh = group !== "" && whole.split(group)[1]?.length === 2;
    return { group, decimal, groupStyle: lakh ? "lakh" : "thousand" };
  } catch {
    return ENGLISH;
  }
};

/** "80596000.00" → "80,596,000.00". Keeps exactly the digits it's given (no rounding). */
export const formatNumberString = (
  value: string,
  separators: NumberSeparators,
): string => {
  const [whole, fraction] = value.split(".");
  const groups =
    separators.groupStyle === "lakh"
      ? /(\d)(?=(\d\d)*\d{3}(?!\d))/g
      : /(\d)(?=(\d{3})+(?!\d))/g;
  const grouped = separators.group
    ? whole.replace(groups, `$1${separators.group}`)
    : whole;
  return fraction === undefined
    ? grouped
    : `${grouped}${separators.decimal}${fraction}`;
};

/** "1 sat", "0 sats", "2 sats" */
export const satsUnit = (sats: number): string =>
  sats === 1 ? "sat" : "sats";
